<template>
  <v-dialog :model-value="modelValue" max-width="900" @update:model-value="$emit('update:modelValue', $event)">
    <v-card class="pa-6">
      <v-card-title>{{ transactionForm.id ? $t('transactions.edit') : $t('transactions.add') }}</v-card-title>
      <v-card-text>
        <!-- Top Row: Date, Internal Toggle, and Client -->
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="transactionForm.transaction_date"
              :label="$t('transactions.date')"
              type="datetime-local"
            />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center justify-center">
            <v-switch
              v-model="isInternal"
              :label="$t('transactions.internal')"
              color="primary"
              @update:model-value="handleInternalToggle"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="transactionForm.client_id"
              v-model:search="clientSearch"
              :items="filteredClients"
              item-title="phone"
              item-value="id"
              :label="$t('transactions.searchClient')"
              :placeholder="$t('transactions.searchClientPlaceholder')"
              :disabled="isInternal"
              clearable
              @update:model-value="(val) => {
                if (val && clients && Array.isArray(clients)) {
                  const client = clients.find(c => c.id === val)
                  clientSearch = client?.phone || ''
                }
              }"
            >
              <template #no-data>
                <div v-if="shouldShowSaveClientButton" class="pa-4">
                  <p class="text-body-2 mb-2">{{ $t('transactions.noClientFound') }}</p>
                  <v-btn size="small" color="primary" @click="saveClientFromSearch">
                    {{ $t('transactions.saveAsNewClient') }}
                  </v-btn>
                </div>
                <div v-else class="pa-2">
                  <p class="text-body-2">{{ $t('transactions.typeAtLeast10Digits') }}</p>
                </div>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        
        <v-row>
          <!-- Left Column: Sold Coin Info -->
          <v-col cols="12" md="6">
            <v-card class="border-title-card">
              <v-card-title class="text-h6 border-title">{{ $t('transactions.sold') }}</v-card-title>
              <v-form>
                <v-autocomplete
                  v-model="transactionForm.from_wallet_id"
                  v-model:search="fromWalletSearch"
                  :items="filteredFromWallets"
                  item-title="display_name"
                  item-value="id"
                  :label="$t('transactions.fromWallet')"
                  :placeholder="$t('transactions.walletSearchPlaceholder')"
                  clearable
                  class="form-field-spacing"
                  auto-select-first
                  @update:model-value="(val) => {
                    if (!val) {
                      fromWalletSearch = ''
                    }
                  }"
                >
                  <template #no-data>
                    <div class="pa-2">{{ $t('transactions.noWalletsFound') }}</div>
                  </template>
                </v-autocomplete>

                <v-select
                  v-model="transactionForm.sold_coin_id"
                  :items="coins"
                  item-title="name"
                  item-value="id"
                  :label="$t('transactions.soldCoin')"
                  class="form-field-spacing"
                >
                  <template #item="{ props, item }">
                    <v-list-item v-bind="props" :title="`${item.raw.symbol} - ${item.raw.name}`"></v-list-item>
                  </template>
                  <template #selection="{ item }">
                    {{ item.raw.symbol }} - {{ item.raw.name }}
                  </template>
                </v-select>

                <v-row class="form-field-spacing" no-gutters>
                  <v-col cols="8" class="pr-1">
                    <v-text-field 
                      :model-value="fromWalletBalance"
                      :label="$t('transactions.walletBalance')"
                      readonly
                    />
                  </v-col>
                  <v-col cols="4" class="pl-1">
                    <v-text-field 
                      v-model="transactionForm.sold_rate" 
                      :label="$t('transactions.soldRate')" 
                      type="number" 
                      readonly 
                    />
                  </v-col>
                </v-row>
                <v-row class="form-field-spacing" no-gutters>
                  <v-col cols="8" class="pr-1">
                    <v-text-field 
                      v-model="transactionForm.sold_amount" 
                      :label="$t('transactions.soldAmount')" 
                      type="number"
                      @update:model-value="handleSoldAmountInput"
                    />
                  </v-col>
                  <v-col cols="4" class="pl-1">
                    <v-text-field 
                      :model-value="soldAmountUsdt.toString()"
                      :label="$t('transactions.inUsdt')"
                      readonly
                    />
                  </v-col>
                </v-row>
                <v-text-field 
                  v-model="transactionForm.send_fee" 
                  :label="$t('transactions.sendFee')" 
                  type="number" 
                  class="form-field-spacing" 
                />
              </v-form>
            </v-card>
          </v-col>
          
          <!-- Right Column: Received Coin Info -->
          <v-col cols="12" md="6">
            <v-card class="border-title-card">
              <v-card-title class="text-h6 border-title">{{ $t('transactions.received') }}</v-card-title>
              <v-form>
                <v-autocomplete
                  v-model="transactionForm.to_wallet_id"
                  v-model:search="toWalletSearch"
                  :items="filteredToWallets"
                  item-title="display_name"
                  item-value="id"
                  :label="$t('transactions.toWallet')"
                  :placeholder="$t('transactions.walletSearchPlaceholder')"
                  clearable
                  class="form-field-spacing"
                  auto-select-first
                  @update:model-value="(val) => {
                    if (!val) {
                      toWalletSearch = ''
                    }
                  }"
                >
                  <template #no-data>
                    <div class="pa-2">{{ $t('transactions.noWalletsFound') }}</div>
                  </template>
                </v-autocomplete>
                <v-select
                  v-model="transactionForm.received_coin_id"
                  :items="coins"
                  item-title="name"
                  item-value="id"
                  :label="$t('transactions.receivedCoin')"
                  class="form-field-spacing"
                >
                  <template #item="{ props, item }">
                    <v-list-item v-bind="props" :title="`${item.raw.symbol} - ${item.raw.name}`"></v-list-item>
                  </template>
                  <template #selection="{ item }">
                    {{ item.raw.symbol }} - {{ item.raw.name }}
                  </template>
                </v-select>
                <v-row class="form-field-spacing" no-gutters>
                  <v-col cols="8" class="pr-1">
                    <v-text-field 
                      v-model="transactionForm.received_amount" 
                      :label="$t('transactions.receivedAmount')" 
                      type="number"
                    />
                  </v-col>
                  <v-col cols="4" class="pl-1">
                    <v-text-field 
                      :model-value="receivedAmountUsdt.toString()"
                      :label="$t('transactions.usdt')"
                      readonly
                    />
                  </v-col>
                </v-row>
                <v-row class="form-field-spacing" no-gutters>
                  <v-col cols="6" class="pr-1">
                    <v-text-field 
                      v-model="transactionForm.received_rate" 
                      :label="$t('transactions.receivedRate')" 
                      type="number" 
                    />
                  </v-col>
                  <v-col cols="6" class="pl-1">
                    <v-text-field 
                      v-model="transactionForm.receive_our_rate" 
                      :label="$t('transactions.receiveOurRate')" 
                      type="number" 
                    />
                  </v-col>
                </v-row>
                <v-text-field 
                  v-model="transactionForm.estimated_profit" 
                  :label="$t('transactions.profit')" 
                  type="number" 
                  class="form-field-spacing" 
                />
              </v-form>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="px-2 pb-4">
        <v-row class="mx-0">
          <v-col cols="12" md="6" class="py-0 pl-0">
            <v-select
              v-model="transactionForm.user_id"
              :items="users"
              item-title="email"
              item-value="id"
              :label="$t('transactions.operator')"
              clearable
            />
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-end align-center pa-0">
            <v-btn @click="handleCancel" class="mr-2">{{ $t('common.cancel') }}</v-btn>
            <v-btn color="primary" @click="$emit('save', transactionForm)">{{ transactionForm.id ? $t('transactions.update') : $t('common.save') }}</v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, getErrorMessage } from '../../utils/api'
import { dateStringToISO, formatISOToDateTimeLocal, getTodayDateTimeLocal } from '../../utils/date'

const { t } = useI18n()

interface Props {
  modelValue: boolean
  transaction?: any
  coins: any[]
  wallets: any[]
  users: any[]
  clients: any[]
  currentUserId?: number | null
}

const props = withDefaults(defineProps<Props>(), {
  transaction: null,
  coins: () => [],
  wallets: () => [],
  users: () => [],
  clients: () => [],
  currentUserId: null
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'save': [transaction: any]
  'cancel': []
}>()

const clientSearch = ref('')
const fromWalletSearch = ref('')
const toWalletSearch = ref('')
const isCalculatingReceivedAmount = ref(false)
const isCalculatingProfit = ref(false)
const isCalculatingReceiveOurRate = ref(false)
const isInternal = ref(false)
const isInitializing = ref(false) // Flag to prevent recalculation during initialization

const buildEmptyForm = () => ({
  id: null as number | null,
  client_id: null as number | null,
  from_wallet_id: null as number | null,
  sold_coin_id: null as number | null,
  sold_amount: 0,
  sold_rate: 0,
  to_wallet_id: null as number | null,
  received_coin_id: null as number | null,
  received_amount: 0,
  received_rate: 0,
  user_id: props.currentUserId ?? null,
  send_fee: 0,
  receive_our_rate: 0,
  estimated_profit: 0,
  transaction_date: getTodayDateTimeLocal(),
})

const transactionForm = ref(buildEmptyForm())

const filteredClients = computed(() => {
  if (!clientSearch.value) return props.clients
  const search = clientSearch.value.toLowerCase()
  return props.clients.filter(client => 
    client.phone.toLowerCase().includes(search)
  )
})

const filteredFromWallets = computed(() => {
  if (!props.wallets || props.wallets.length === 0) return []
  if (!fromWalletSearch.value) return props.wallets
  const search = fromWalletSearch.value.toLowerCase()
  return props.wallets.filter(wallet => {
    const displayName = wallet.display_name || (wallet.exchange?.name ? `${wallet.exchange.name} - ${wallet.coin?.symbol}` : wallet.coin?.symbol) || ''
    return displayName.toLowerCase().includes(search) ||
      wallet.exchange?.name?.toLowerCase().includes(search) ||
      wallet.coin?.symbol?.toLowerCase().includes(search) ||
      wallet.coin?.name?.toLowerCase().includes(search)
  })
})

const filteredToWallets = computed(() => {
  if (!props.wallets || props.wallets.length === 0) return []
  if (!toWalletSearch.value) return props.wallets
  const search = toWalletSearch.value.toLowerCase()
  return props.wallets.filter(wallet => {
    const displayName = wallet.display_name || (wallet.exchange?.name ? `${wallet.exchange.name} - ${wallet.coin?.symbol}` : wallet.coin?.symbol) || ''
    return displayName.toLowerCase().includes(search) ||
      wallet.exchange?.name?.toLowerCase().includes(search) ||
      wallet.coin?.symbol?.toLowerCase().includes(search) ||
      wallet.coin?.name?.toLowerCase().includes(search)
  })
})

const fromWalletBalance = ref('')

const soldAmountUsdt = computed(() => {
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const sendFee = parseFloat(transactionForm.value.send_fee?.toString() || '0')
  if (soldAmount > 0 && soldRate > 0) {
    const usdtValue = (soldAmount - sendFee) * soldRate
    return Math.round(usdtValue * 100000000) / 100000000
  }
  return 0
})

const receivedAmountUsdt = computed(() => {
  const receivedAmount = parseFloat(transactionForm.value.received_amount?.toString() || '0')
  const receivedRate = parseFloat(transactionForm.value.receive_our_rate?.toString() || '0')
  if (receivedAmount > 0 && receivedRate > 0) {
    const usdtValue = receivedAmount * receivedRate
    return Math.round(usdtValue * 100000000) / 100000000
  }
  return 0
})

const shouldShowSaveClientButton = computed(() => {
  return clientSearch.value && clientSearch.value.length >= 10 && !transactionForm.value.client_id && !isInternal.value
})

const handleInternalToggle = (value: boolean | null) => {
  if (value) {
    transactionForm.value.client_id = 1
    clientSearch.value = ''
  } else {
    transactionForm.value.client_id = null
    clientSearch.value = ''
  }
}

// Optional symbol (e.g. 'USDT') to default rate to 1 when API returns no rate
const fetchRateFromDB = async (coinId: number | null, created_at?: string | null, symbol?: string | null) => {
  if (!coinId) return null
  
  try {
    let url = `/api/coin_rates/latest?coin_id=${coinId}`
    if (created_at) {
      url += `&date_before=${created_at}`
    }
    const response = await apiGet(url)
    if (response.ok) {
      const rateData = await response.json()
      const rate = rateData.rate != null && Number(rateData.rate) > 0 ? Number(rateData.rate) : null
      // USDT (and other stablecoins at 1:1) often have no rate in DB - default to 1 so received_amount is calculated
      if (rate == null && (symbol?.toUpperCase() === 'USDT' || symbol?.toUpperCase() === 'USDC')) {
        return 1
      }
      return rate
    }
  } catch (error) {
    console.error('Fetch rate from DB error:', error)
  }
  // USDT/USDC: no rate in DB => use 1 so BTC->USDT received_amount is correct
  if (symbol?.toUpperCase() === 'USDT' || symbol?.toUpperCase() === 'USDC') {
    return 1
  }
  return null
}

const saveClientFromSearch = async () => {
  if (!clientSearch.value || clientSearch.value.length < 10) {
    alert(t('transactions.enterAtLeast10Digits'))
    return
  }
  
  try {
    const response = await apiPost('/api/clients', { client: { phone: clientSearch.value } })
    if (response.ok) {
      const newClient = await response.json()
      transactionForm.value.client_id = newClient.id
      emit('save', transactionForm.value) // Trigger reload of clients in parent
      // Update search to show the new client
      const client = props.clients.find(c => c.id === newClient.id)
      if (client) {
        clientSearch.value = client.phone
      }
    } else {
      const errors = await getErrorMessage(response, t('errors.unknownError'))
      alert(t('transactions.failedToCreateClient', { errors }))
    }
  } catch (error) {
    console.error('Save client from search error:', error)
    alert(t('transactions.errorCreatingClient'))
  }
}

const calculateReceivedAmount = () => {
  if (isCalculatingReceivedAmount.value) return
  
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  let receivedRate = parseFloat(transactionForm.value.received_rate?.toString() || '0')
  // USDT/USDC often have no rate in DB - use 1 so BTC->USDT received_amount is correct
  if (receivedRate <= 0 && transactionForm.value.received_coin_id && props.coins?.length) {
    const coin = props.coins.find((c: any) => c.id === transactionForm.value.received_coin_id)
    if (coin?.symbol?.toUpperCase() === 'USDT' || coin?.symbol?.toUpperCase() === 'USDC') {
      receivedRate = 1
    }
  }
  
  if (soldAmount > 0 && soldRate > 0 && receivedRate > 0) {
    isCalculatingReceivedAmount.value = true
    const calculatedAmount = (soldAmount * soldRate) / receivedRate
    transactionForm.value.received_amount = Math.round(calculatedAmount * 100000000) / 100000000
    setTimeout(() => {
      isCalculatingReceivedAmount.value = false
    }, 0)
  } else if (soldAmount === 0 || soldRate === 0 || receivedRate === 0) {
    transactionForm.value.received_amount = 0
  }
}

const calculateEstimatedProfit = () => {
  if (isCalculatingProfit.value) return
  
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const receivedAmount = parseFloat(transactionForm.value.received_amount?.toString() || '0')
  const receivedRate = parseFloat(transactionForm.value.receive_our_rate?.toString() || transactionForm.value.received_rate?.toString() || '0')
  const sendFee = parseFloat(transactionForm.value.send_fee?.toString() || '0')
  
  if (soldAmount > 0 && soldRate > 0 && receivedAmount > 0 && receivedRate > 0) {
    isCalculatingProfit.value = true
    const soldValue = soldAmount * soldRate
    const receivedValue = receivedAmount * receivedRate
    const profit = receivedValue - soldValue - sendFee * soldRate
    transactionForm.value.estimated_profit = Math.round(profit * 100000000) / 100000000
    setTimeout(() => {
      isCalculatingProfit.value = false
    }, 0)
  } else {
    transactionForm.value.estimated_profit = 0
  }
}

const calculateReceiveOurRate = () => {
  if (isCalculatingReceiveOurRate.value || isInitializing.value) return // Skip during initialization
  
  // Don't recalculate for existing transactions - preserve the stored value
  if (transactionForm.value.id) return
  
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const receivedAmount = parseFloat(transactionForm.value.received_amount?.toString() || '0')
  
  if (soldAmount > 0 && soldRate > 0 && receivedAmount > 0) {
    isCalculatingReceiveOurRate.value = true
    const calculatedRate = (soldAmount * soldRate) / receivedAmount
    transactionForm.value.receive_our_rate = Math.round(calculatedRate * 100000000) / 100000000
    setTimeout(() => {
      isCalculatingReceiveOurRate.value = false
    }, 0)
  } else if (soldAmount === 0 || soldRate === 0 || receivedAmount === 0) {
    // Only clear if we're not initializing (to preserve stored value)
    if (!isInitializing.value) {
      transactionForm.value.receive_our_rate = 0
    }
  }
}

const handleSoldAmountInput = () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
  setTimeout(() => {
    calculateReceiveOurRate()
  }, 0)
}

const refreshFromWalletBalance = async () => {
  if (!transactionForm.value.from_wallet_id) {
    fromWalletBalance.value = ''
    return
  }

  try {
    const params = new URLSearchParams()
    if (transactionForm.value.transaction_date) {
      params.append('date_to', transactionForm.value.transaction_date)
    }

    let url = `/api/wallets/${transactionForm.value.from_wallet_id}`
    const query = params.toString()
    if (query) {
      url += `?${query}`
    }

    const response = await apiGet(url)
    if (response.ok) {
      const data = await response.json()
      const balanceValue = data.balance_breakdown?.ending_balance ?? data.wallet?.calculated_balance ?? ''
      fromWalletBalance.value = balanceValue !== '' ? balanceValue.toString() : ''
    } else {
      // Fallback to the current calculated balance from props if available
      const wallet = props.wallets.find(w => w.id === transactionForm.value.from_wallet_id)
      const balance = wallet?.calculated_balance ?? wallet?.balance ?? ''
      fromWalletBalance.value = balance !== '' ? balance.toString() : ''
    }
  } catch (error) {
    console.error('Fetch wallet balance error:', error)
    const wallet = props.wallets.find(w => w.id === transactionForm.value.from_wallet_id)
    const balance = wallet?.calculated_balance ?? wallet?.balance ?? ''
    fromWalletBalance.value = balance !== '' ? balance.toString() : ''
  }
}

// Watch for wallet changes and automatically set corresponding coin
watch(() => transactionForm.value.from_wallet_id, async (newWalletId, oldWalletId) => {
  if (!newWalletId || isInitializing.value) return // Skip during initialization
  
  if (props.wallets && props.wallets.length > 0) {
    const wallet = props.wallets.find(w => w.id === newWalletId)
    if (wallet && wallet.coin && wallet.coin.id) {
      // Only set coin and rate if wallet actually changed (not during initialization)
      if (oldWalletId !== newWalletId) {
        transactionForm.value.sold_coin_id = wallet.coin.id
        const created_at = transactionForm.value.transaction_date 
          ? dateStringToISO(transactionForm.value.transaction_date)
          : null
        const rate = await fetchRateFromDB(wallet.coin.id, created_at)
        if (rate) {
          transactionForm.value.sold_rate = rate
        }
      }
    }
  }
  refreshFromWalletBalance()
})

watch(() => transactionForm.value.to_wallet_id, async (newWalletId, oldWalletId) => {
  if (!newWalletId || isInitializing.value) return // Skip during initialization
  
  if (props.wallets && props.wallets.length > 0) {
    const wallet = props.wallets.find(w => w.id === newWalletId)
    if (wallet && wallet.coin && wallet.coin.id) {
      // Only set coin and rate if wallet actually changed (not during initialization)
      if (oldWalletId !== newWalletId) {
        transactionForm.value.received_coin_id = wallet.coin.id
        const created_at = transactionForm.value.transaction_date 
          ? dateStringToISO(transactionForm.value.transaction_date)
          : null
        const symbol = wallet.coin.symbol ?? null
        const rate = await fetchRateFromDB(wallet.coin.id, created_at, symbol)
        if (rate != null) {
          transactionForm.value.received_rate = rate
        }
      }
    }
  }
})

watch(() => transactionForm.value.sold_coin_id, async (newCoinId) => {
  if (!newCoinId || isInitializing.value) return // Skip during initialization
  
  const created_at = transactionForm.value.transaction_date 
    ? new Date(transactionForm.value.transaction_date).toISOString()
    : null
  const rate = await fetchRateFromDB(newCoinId, created_at)
  if (rate) {
    transactionForm.value.sold_rate = rate
  }
  // Only recalculate receive_our_rate for new transactions
  if (!transactionForm.value.id) {
    setTimeout(() => {
      calculateReceiveOurRate()
    }, 100)
  }
})

watch(() => transactionForm.value.received_coin_id, async (newCoinId) => {
  if (!newCoinId || isInitializing.value) return // Skip during initialization
  
  const symbol = props.coins?.find((c: any) => c.id === newCoinId)?.symbol ?? null
  const created_at = transactionForm.value.transaction_date 
    ? new Date(transactionForm.value.transaction_date).toISOString()
    : null
  const rate = await fetchRateFromDB(newCoinId, created_at, symbol)
  if (rate != null) {
    transactionForm.value.received_rate = rate
  }
  // Only recalculate receive_our_rate for new transactions
  if (!transactionForm.value.id) {
    setTimeout(() => {
      calculateReceiveOurRate()
    }, 100)
  }
})

watch(() => transactionForm.value.transaction_date, async (newDate) => {
  if (isInitializing.value) return // Skip during initialization
  
  if (newDate && transactionForm.value.sold_coin_id) {
    const created_at = new Date(newDate).toISOString()
    const symbol = props.coins?.find((c: any) => c.id === transactionForm.value.sold_coin_id)?.symbol ?? null
    const rate = await fetchRateFromDB(transactionForm.value.sold_coin_id, created_at, symbol)
    if (rate != null) {
      transactionForm.value.sold_rate = rate
    }
  }
  if (newDate && transactionForm.value.received_coin_id) {
    const created_at = new Date(newDate).toISOString()
    const symbol = props.coins?.find((c: any) => c.id === transactionForm.value.received_coin_id)?.symbol ?? null
    const rate = await fetchRateFromDB(transactionForm.value.received_coin_id, created_at, symbol)
    if (rate != null) {
      transactionForm.value.received_rate = rate
    }
  }
  refreshFromWalletBalance()
})

watch(() => transactionForm.value.sold_amount, () => {
  if (isInitializing.value) return // Skip during initialization
  
  calculateReceivedAmount()
  calculateEstimatedProfit()
  // Only recalculate receive_our_rate for new transactions
  if (!transactionForm.value.id) {
    setTimeout(() => {
      calculateReceiveOurRate()
    }, 0)
  }
})

watch(() => transactionForm.value.sold_rate, () => {
  if (isInitializing.value) return // Skip during initialization
  
  calculateReceivedAmount()
  calculateEstimatedProfit()
  // Only recalculate receive_our_rate for new transactions
  if (!transactionForm.value.id) {
    setTimeout(() => {
      calculateReceiveOurRate()
    }, 0)
  }
})

watch(() => transactionForm.value.received_amount, () => {
  if (isInitializing.value) return // Skip during initialization
  
  calculateEstimatedProfit()
  // Only recalculate receive_our_rate for new transactions
  if (!transactionForm.value.id) {
    calculateReceiveOurRate()
  }
})

watch(() => transactionForm.value.received_rate, () => {
  if (isInitializing.value) return // Skip during initialization
  
  calculateReceivedAmount()
  calculateEstimatedProfit()
  // Only recalculate receive_our_rate for new transactions
  if (!transactionForm.value.id) {
    setTimeout(() => {
      calculateReceiveOurRate()
    }, 0)
  }
})

watch(() => transactionForm.value.receive_our_rate, () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
})

watch(() => transactionForm.value.send_fee, () => {
  calculateEstimatedProfit()
})

// Initialize form when transaction prop changes or dialog opens
watch(() => props.transaction, (newTransaction) => {
  isInitializing.value = true // Set flag to prevent recalculation
  
  if (newTransaction) {
    let created_at = newTransaction.created_at
    const transactionDate = created_at ? formatISOToDateTimeLocal(created_at) : null

    transactionForm.value = {
      id: newTransaction.id,
      client_id: newTransaction.client_id,
      user_id: newTransaction.user_id,
      from_wallet_id: newTransaction.from_wallet_id,
      sold_coin_id: newTransaction.sold_coin_id,
      sold_amount: newTransaction.sold_amount,
      sold_rate: newTransaction.sold_rate,
      to_wallet_id: newTransaction.to_wallet_id,
      received_coin_id: newTransaction.received_coin_id,
      received_amount: newTransaction.received_amount,
      received_rate: newTransaction.received_rate,
      send_fee: newTransaction.send_fee || 0,
      receive_our_rate: newTransaction.receive_our_rate || 0,
      estimated_profit: newTransaction.estimated_profit || 0,
      transaction_date: transactionDate || getTodayDateTimeLocal(),
    }
    
    // Set internal toggle based on client_id
    isInternal.value = newTransaction.client_id === 1
    
    // Find the client phone for the search field
    if (props.clients && Array.isArray(props.clients) && newTransaction.client_id !== 1) {
      const client = props.clients.find(c => c.id === newTransaction.client_id)
      clientSearch.value = client?.phone || ''
    } else {
      clientSearch.value = ''
    }
    
    // Find wallet display names for the search fields
    if (props.wallets && Array.isArray(props.wallets)) {
      const fromWallet = props.wallets.find(w => w.id === newTransaction.from_wallet_id)
      fromWalletSearch.value = fromWallet?.display_name || ''
      const toWallet = props.wallets.find(w => w.id === newTransaction.to_wallet_id)
      toWalletSearch.value = toWallet?.display_name || ''
    }

    refreshFromWalletBalance()
  } else {
    transactionForm.value = buildEmptyForm()
    isInternal.value = false
    clientSearch.value = ''
    fromWalletSearch.value = ''
    toWalletSearch.value = ''
    fromWalletBalance.value = ''
  }
  
  // Reset flag after a short delay to allow all watchers to complete
  setTimeout(() => {
    isInitializing.value = false
  }, 100)
}, { immediate: true })

const handleCancel = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    isInitializing.value = true // Set flag to prevent recalculation
    
    // When dialog opens, re-initialize form from transaction prop if it exists
    if (props.transaction) {
      let created_at = props.transaction.created_at
      const transactionDate = created_at ? formatISOToDateTimeLocal(created_at) : null

      transactionForm.value = {
        id: props.transaction.id,
        client_id: props.transaction.client_id,
        user_id: props.transaction.user_id,
        from_wallet_id: props.transaction.from_wallet_id,
        sold_coin_id: props.transaction.sold_coin_id,
        sold_amount: props.transaction.sold_amount,
        sold_rate: props.transaction.sold_rate,
        to_wallet_id: props.transaction.to_wallet_id,
        received_coin_id: props.transaction.received_coin_id,
        received_amount: props.transaction.received_amount,
        received_rate: props.transaction.received_rate,
        send_fee: props.transaction.send_fee || 0,
        receive_our_rate: props.transaction.receive_our_rate || 0,
        estimated_profit: props.transaction.estimated_profit || 0,
        transaction_date: transactionDate || getTodayDateTimeLocal(),
      }
      
      // Set internal toggle based on client_id
      isInternal.value = props.transaction.client_id === 1
      
      // Find the client phone for the search field
      if (props.clients && Array.isArray(props.clients) && props.transaction.client_id !== 1) {
        const client = props.clients.find(c => c.id === props.transaction.client_id)
        clientSearch.value = client?.phone || ''
      } else {
        clientSearch.value = ''
      }
      
      // Find wallet display names for the search fields
      if (props.wallets && Array.isArray(props.wallets)) {
        const fromWallet = props.wallets.find(w => w.id === props.transaction.from_wallet_id)
        fromWalletSearch.value = fromWallet?.display_name || ''
        const toWallet = props.wallets.find(w => w.id === props.transaction.to_wallet_id)
        toWalletSearch.value = toWallet?.display_name || ''
      }

      refreshFromWalletBalance()
    } else {
      // When dialog opens for new transaction, ensure user_id is set if currentUserId is available
      if (!transactionForm.value.user_id && props.currentUserId) {
        transactionForm.value.user_id = props.currentUserId
      }
    }
    
    // Reset flag after a short delay to allow all watchers to complete
    setTimeout(() => {
      isInitializing.value = false
    }, 100)
  } else {
    // Reset form when dialog closes
    isInitializing.value = false
    isInternal.value = false
    clientSearch.value = ''
    fromWalletSearch.value = ''
    toWalletSearch.value = ''
    transactionForm.value = buildEmptyForm()
    fromWalletBalance.value = ''
  }
})

// Watch for currentUserId changes and update form if user_id is not set
watch(() => props.currentUserId, (newUserId) => {
  if (newUserId && !transactionForm.value.user_id && !transactionForm.value.id) {
    transactionForm.value.user_id = newUserId
  }
})
</script>

<style scoped>
/* Column and card layout */
:deep(.v-col) {
  overflow: visible !important;
  display: flex;
}

:deep(.v-col) > .v-card {
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* Border title card styling */
.border-title-card {
  position: relative;
  padding-top: 20px;
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 16px;
  overflow: visible !important;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: none !important;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  box-shadow: none !important;
}

.border-title-card :deep(.v-card) {
  overflow: visible !important;
  border: none !important;
  box-shadow: none !important;
}

.border-title-card :deep(.v-form) {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Border title text positioning */
.border-title {
  position: absolute;
  top: -24px;
  left: 8px;
  background: rgb(var(--v-theme-background));
  padding: 4px 12px;
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.5;
  z-index: 10;
  white-space: nowrap;
  display: inline-block;
}

/* Minimal vertical spacing between form fields */
.form-field-spacing {
  margin-top: 0 !important;
}

.v-card-text{
  padding: 8px !important;
}
</style>

